import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Eye, Users, Globe, Monitor, Smartphone, Tablet, TrendingUp, 
  Clock, MousePointer, ArrowUpRight, ArrowDownRight, Activity,
  BarChart3, PieChart, Target, Layers
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

// Stat Card Component
function StatCard({ title, value, subtitle, icon: Icon, trend, color = "cyan" }) {
  const colorClasses = {
    cyan: "bg-cyan-50 text-cyan-600 border-cyan-200",
    purple: "bg-purple-50 text-purple-600 border-purple-200",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-200",
    amber: "bg-amber-50 text-amber-600 border-amber-200",
    rose: "bg-rose-50 text-rose-600 border-rose-200"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-gray-200 p-5 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2.5 rounded-lg border ${colorClasses[color]}`}>
          <Icon size={20} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-bold ${trend >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {trend >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <h3 className="text-2xl font-outfit font-bold text-gray-900">{value}</h3>
      <p className="text-sm text-gray-500 font-dm-sans">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </motion.div>
  );
}

// Simple Bar Chart Component
function SimpleBarChart({ data, maxValue, color = "#00F0FF" }) {
  const max = maxValue || Math.max(...data.map(d => d.views || d.count || 0), 1);
  
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((item, i) => {
        const value = item.views || item.count || 0;
        const height = (value / max) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${height}%` }}
              transition={{ duration: 0.5, delay: i * 0.03 }}
              className="w-full rounded-t"
              style={{ backgroundColor: color, minHeight: value > 0 ? '4px' : '0' }}
              title={`${item.date || item.hour}: ${value} views`}
            />
            <span className="text-[9px] text-gray-400 truncate w-full text-center">
              {item.date?.split(' ')[1] || item.hour?.split(':')[0]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Horizontal Bar Component
function HorizontalBar({ label, value, maxValue, color = "#00F0FF" }) {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600 font-dm-sans truncate max-w-[200px]">{label}</span>
        <span className="text-gray-900 font-bold font-outfit">{value.toLocaleString()}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.6 }}
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>
    </div>
  );
}

// Funnel Chart Component
function FunnelChart({ data }) {
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const colors = ["#00F0FF", "#8B5CF6", "#10B981", "#F59E0B"];
  
  return (
    <div className="space-y-3">
      {data.map((item, i) => {
        const width = (item.count / maxCount) * 100;
        const conversionRate = i > 0 && data[i-1].count > 0 
          ? ((item.count / data[i-1].count) * 100).toFixed(1) 
          : null;
        
        return (
          <div key={i} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-dm-sans">{item.stage}</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-900 font-bold font-outfit">{item.count.toLocaleString()}</span>
                {conversionRate && (
                  <span className="text-xs text-gray-400">({conversionRate}%)</span>
                )}
              </div>
            </div>
            <div className="h-8 bg-gray-100 rounded overflow-hidden flex items-center justify-center relative">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${width}%` }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="absolute left-0 h-full rounded"
                style={{ backgroundColor: colors[i] }}
              />
              <span className="relative z-10 text-xs font-bold text-white mix-blend-difference">
                {item.stage}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Donut Chart Component
function DonutChart({ data, colors = ["#00F0FF", "#8B5CF6", "#10B981", "#F59E0B", "#EF4444"] }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return <div className="text-center text-gray-400 py-8">No data</div>;
  
  let cumulativePercent = 0;
  const segments = Object.entries(data).map(([key, value], i) => {
    const percent = (value / total) * 100;
    const startPercent = cumulativePercent;
    cumulativePercent += percent;
    return { key, value, percent, startPercent, color: colors[i % colors.length] };
  });

  return (
    <div className="flex items-center gap-6">
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
          {segments.map((seg, i) => (
            <circle
              key={i}
              cx="18"
              cy="18"
              r="14"
              fill="none"
              stroke={seg.color}
              strokeWidth="4"
              strokeDasharray={`${seg.percent} ${100 - seg.percent}`}
              strokeDashoffset={-seg.startPercent}
              className="transition-all duration-500"
            />
          ))}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold font-outfit text-gray-900">{total}</span>
        </div>
      </div>
      <div className="flex-1 space-y-2">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: seg.color }} />
            <span className="text-gray-600 font-dm-sans capitalize">{seg.key}</span>
            <span className="text-gray-900 font-bold ml-auto">{seg.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminWebsiteAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/admin/website-analytics`, authHeader());
      setAnalytics(res.data);
      setError(null);
    } catch (err) {
      setError("Failed to load analytics data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Refresh every 30 seconds for real-time data
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <button onClick={fetchAnalytics} className="text-cyan-600 hover:underline">
          Try Again
        </button>
      </div>
    );
  }

  const { realtime, summary, views_by_day, popular_pages, popular_trainings, 
          traffic_sources, device_breakdown, browser_breakdown, 
          conversion_funnel, hourly_distribution } = analytics || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-gray-900">Website Analytics</h1>
          <p className="text-gray-500 font-dm-sans">Track visitor behavior and engagement</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-emerald-700 font-bold font-outfit">{realtime?.active_visitors || 0}</span>
            <span className="text-emerald-600 text-sm font-dm-sans">online now</span>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          title="Today's Views"
          value={summary?.today_views?.toLocaleString() || 0}
          icon={Eye}
          color="cyan"
        />
        <StatCard
          title="Today's Visitors"
          value={summary?.today_sessions?.toLocaleString() || 0}
          icon={Users}
          color="purple"
        />
        <StatCard
          title="Week Views"
          value={summary?.week_views?.toLocaleString() || 0}
          icon={TrendingUp}
          color="emerald"
        />
        <StatCard
          title="Week Visitors"
          value={summary?.week_sessions?.toLocaleString() || 0}
          icon={Globe}
          color="amber"
        />
        <StatCard
          title="Total Views"
          value={summary?.total_views?.toLocaleString() || 0}
          icon={BarChart3}
          color="cyan"
        />
        <StatCard
          title="Total Visitors"
          value={summary?.total_sessions?.toLocaleString() || 0}
          icon={Activity}
          color="purple"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Page Views Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-bold text-gray-900">Page Views (14 Days)</h3>
            <BarChart3 size={18} className="text-gray-400" />
          </div>
          {views_by_day?.length > 0 ? (
            <SimpleBarChart data={views_by_day} color="#00F0FF" />
          ) : (
            <div className="h-32 flex items-center justify-center text-gray-400">No data yet</div>
          )}
        </motion.div>

        {/* Hourly Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-bold text-gray-900">Traffic by Hour</h3>
            <Clock size={18} className="text-gray-400" />
          </div>
          {hourly_distribution?.length > 0 ? (
            <SimpleBarChart data={hourly_distribution} color="#8B5CF6" />
          ) : (
            <div className="h-32 flex items-center justify-center text-gray-400">No data yet</div>
          )}
        </motion.div>
      </div>

      {/* Middle Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversion Funnel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-bold text-gray-900">Conversion Funnel</h3>
            <Target size={18} className="text-gray-400" />
          </div>
          {conversion_funnel?.length > 0 ? (
            <FunnelChart data={conversion_funnel} />
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400">No data yet</div>
          )}
        </motion.div>

        {/* Device Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-bold text-gray-900">Devices</h3>
            <Monitor size={18} className="text-gray-400" />
          </div>
          {Object.keys(device_breakdown || {}).length > 0 ? (
            <DonutChart data={device_breakdown} colors={["#00F0FF", "#8B5CF6", "#10B981"]} />
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400">No data yet</div>
          )}
        </motion.div>

        {/* Browser Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-bold text-gray-900">Browsers</h3>
            <Globe size={18} className="text-gray-400" />
          </div>
          {Object.keys(browser_breakdown || {}).length > 0 ? (
            <DonutChart data={browser_breakdown} colors={["#F59E0B", "#EF4444", "#8B5CF6", "#10B981", "#00F0FF"]} />
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400">No data yet</div>
          )}
        </motion.div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Popular Pages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-bold text-gray-900">Popular Pages</h3>
            <MousePointer size={18} className="text-gray-400" />
          </div>
          <div className="space-y-3">
            {popular_pages?.length > 0 ? (
              popular_pages.slice(0, 6).map((page, i) => (
                <HorizontalBar
                  key={i}
                  label={page.page === "/" ? "Home" : page.page}
                  value={page.views}
                  maxValue={popular_pages[0]?.views || 1}
                  color="#00F0FF"
                />
              ))
            ) : (
              <div className="text-center text-gray-400 py-4">No data yet</div>
            )}
          </div>
        </motion.div>

        {/* Popular Trainings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-bold text-gray-900">Popular Trainings</h3>
            <Layers size={18} className="text-gray-400" />
          </div>
          <div className="space-y-3">
            {popular_trainings?.length > 0 ? (
              popular_trainings.map((training, i) => (
                <HorizontalBar
                  key={i}
                  label={training.title}
                  value={training.views}
                  maxValue={popular_trainings[0]?.views || 1}
                  color="#8B5CF6"
                />
              ))
            ) : (
              <div className="text-center text-gray-400 py-4">No data yet</div>
            )}
          </div>
        </motion.div>

        {/* Traffic Sources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-bold text-gray-900">Traffic Sources</h3>
            <TrendingUp size={18} className="text-gray-400" />
          </div>
          <div className="space-y-3">
            {traffic_sources?.length > 0 ? (
              traffic_sources.slice(0, 6).map((source, i) => (
                <HorizontalBar
                  key={i}
                  label={source.source}
                  value={source.visits}
                  maxValue={traffic_sources[0]?.visits || 1}
                  color="#10B981"
                />
              ))
            ) : (
              <div className="text-center text-gray-400 py-4">No referrer data yet</div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
